// src/hooks/useEmployeeData.js
import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs
} from 'firebase/firestore';
import { tryResolveTenantId } from '../utils/tenant';

export const useEmployeeData = (employeeId) => {
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!employeeId) {
      setLoading(false);
      return;
    }

  const fetchEmployeeData = async () => {
      setLoading(true);
      setError(null);

      try {
    // Resolve tenant and fetch tenant-scoped locations
    const tenantId = await tryResolveTenantId();
    if (!tenantId) throw new Error('Tenant not resolved');

    // Fetch locations first
    const locationsRef = collection(db, `tenants/${tenantId}/locations`);
    const locationsSnapshot = await getDocs(locationsRef);
        const locationsData = locationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setLocations(locationsData);
        
        // Use the absolute simplest query - just employeeId
    const shiftsRef = collection(db, `tenants/${tenantId}/shifts`);
    const shiftsQuery = query(shiftsRef, where('employeeId', '==', employeeId));

    const shiftsSnapshot = await getDocs(shiftsQuery);
        const allShifts = shiftsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter in memory to avoid index issues and enhance with location names
        const validShifts = allShifts.filter(shift => {
          const notDeleted = shift.deleted !== true;
          const isPublished = shift.isPublished === true || shift.isPublished === undefined;
          return notDeleted && isPublished;
        }).map(shift => {
          // Enhance shift with location name
          const location = locationsData.find(loc => loc.id === shift.locationId);
          return {
            ...shift,
            locationName: location?.name || 'Location TBD'
          };
        });

        setShifts(validShifts);

        // Skip other collections to avoid index issues
        setTimeOffRequests([]);
        setAttendance([]);

      } catch (err) {
        setError(err.message);
        setShifts([]);
        setTimeOffRequests([]);
        setAttendance([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [employeeId]);

  return { 
    timeOffRequests, 
    shifts, 
    attendance, 
    locations,
    loading, 
    error 
  };
};
